import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';

import { DSP_NAMES_FORMATTED } from 'config/constants';
import { selectUser } from 'redux/selectors';

import { TextEmphasis } from '@protonradio/proton-ui';
import DspConnection from './DspConnection';
import DspOptionCard from './DspOptionCard';

import {
  canUserBypassDsp,
  canUserSeeSupportCard,
  getMissingDspEmailHref
} from './helpers';

/**
 * DSP services may render and info or alternate selection depending on
 * a multitude of factors. In order to remove some complexity from the DspConnection component, we've placed
 * that conditional logic here.
 *
 * Cards:
 * [Contact Support] - Instructs user to contact support if no DSP found. Not shown if user can create a new profile
 * or bypass the dsp
 *
 * [Create New Profile] - Clickable option where new DSP profile will be created when the release is created.
 * Currently this is only available via the labelRelease context where the 'releaseId' is passed in as a url
 * query param.
 *
 * [Bypass DSP Selection] - Clickable option where user can skip this step (selecting a DSP profile). Only allowed
 * if an admin set bypass config flag is set in the DB.
 */

const DspConnectionConditionalCard = ({
  artistProfile,
  canCreateDspProfile,
  onSelect,
  selectedValue,
  service,
  dspArtistWithMatchingNameExists
}) => {
  const serviceName = DSP_NAMES_FORMATTED[service];
  const user = useSelector(selectUser);

  // ** Determine which cards should be shown **
  const showBypassCard = canUserBypassDsp(service, artistProfile);

  const showContactSupportCard = canUserSeeSupportCard(
    artistProfile,
    canCreateDspProfile,
    service
  );

  if (showContactSupportCard) {
    const supportEmailHref = getMissingDspEmailHref(serviceName, user, artistProfile);

    return (
      <DspOptionCard
        title={`Can't find the artist's ${serviceName} profile?`}
        testId={DspConnection.TEST_IDS.CONTACT_SUPPORT}
      >
        Contact us at <a href={supportEmailHref}>support@protonradio.com</a> and
        we&apos;ll help with this step.
      </DspOptionCard>
    );
  }

  const clickableCardProps = {
    onSelect,
    selectedValue
  };

  if (canCreateDspProfile) {
    return (
      <DspOptionCard
        title={
          dspArtistWithMatchingNameExists
            ? 'None of these artists match?'
            : `Can't find the artist's ${serviceName} Profile?`
        }
        testId={DspConnection.TEST_IDS.NEW_PROFILE_SELECT}
        value={DspConnection.VALUES.NEW_ARTIST_SELECTION}
        onSelect={onSelect}
        selectedValue={selectedValue}
      >
        Click here and we&apos;ll create a new {serviceName} profile for{' '}
        <TextEmphasis>{artistProfile.name}</TextEmphasis>.
      </DspOptionCard>
    );
  }

  if (showBypassCard) {
    return (
      <DspOptionCard
        title={`Can't find the artist's ${serviceName} profile?`}
        testId={DspConnection.TEST_IDS.SKIP_DSP_CONNECT}
        value={DspConnection.VALUES.SKIP_ARTIST_SELECTION}
        {...clickableCardProps}
      >
        Don&apos;t worry, the artist can connect their profile later!
      </DspOptionCard>
    );
  }

  return null;
};

DspConnectionConditionalCard.propTypes = {
  artistProfile: PropTypes.shape(),
  canCreateDspProfile: PropTypes.bool,
  onSelect: PropTypes.func,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  service: PropTypes.string,
  dspArtistWithMatchingNameExists: PropTypes.bool
};

export default DspConnectionConditionalCard;
